import React, { useEffect, useState } from "react";
import configData from "../../config.json";
import { useNavigate } from "react-router-dom";
import OTPInput from "../Components/OTPInput";
import AlertShow from "../Components/AlertShow";
import {
  isRoleFourTrialActive,
  isSubscriptionActive,
  storePatientSession,
} from "../../common/auth/session";
import "./Signup.css";

function Loginform() {
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ identifier: "", password: "" });
  const [otpPhone, setOtpPhone] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [otpStarted, setOtpStarted] = useState(false);
  const [invalidOTP, setInvalidOTP] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [showAlert, setShowAlert] = useState(false);
  const [severity, setSeverity] = useState("");
  const [alertMsg, setAlertMsg] = useState("");

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    const syncNow = () => setNow(Date.now());
    document.addEventListener("visibilitychange", syncNow);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", syncNow);
    };
  }, []);

  const secondsLeft = (deadline) =>
    Math.max(0, Math.ceil((deadline - now) / 1000));

  const isMobileNumber = (value) => /^\d{10}$/.test(value.trim());

  const handleAlertMsg = (errorType, msg) => {
    setShowAlert(true);
    setSeverity(errorType);
    setAlertMsg(msg);
    setTimeout(() => setShowAlert(false), 2500);
  };

  const resetOtpFields = () => {
    setInvalidOTP(false);
    setResetKey((prevKey) => prevKey + 1);
  };

  const authPost = async (path, body) => {
    const response = await fetch(`${configData.SERVER_API_URL}/auth/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      const error = new Error(data.error || data.message || "Request failed");
      error.data = data;
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const goAfterLogin = (user) => {
    const userRoleId = Number(user.role_id);
    if (userRoleId === 4) {
      navigate(
        isSubscriptionActive(user.subscription_status) ||
          isRoleFourTrialActive(user.created_date)
          ? "/patient/Dashboard"
          : "/tracker-user"
      );
      return;
    }
    navigate(`/?userId=${user.id}`);
  };

  const finishSession = (data) => {
    if (data.status !== "authenticated" || !storePatientSession(data)) {
      handleAlertMsg("error", "Login response was incomplete. Please try again.");
      return;
    }
    handleAlertMsg("success", "Login successful...");
    goAfterLogin(data.user);
  };

  const handleAuthResponse = (data) => {
    if (data.status === "select_account") {
      setAccounts(data.accounts || []);
      handleAlertMsg("success", "Please select the account to continue.");
      return;
    }
    finishSession(data);
  };

  const handleLogin = async (e, selectedUserId) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setAccounts([]);
    try {
      const data = await authPost("login", {
        identifier: loginForm.identifier.trim(),
        password: loginForm.password,
        ...(selectedUserId ? { user_id: selectedUserId } : {}),
      });
      handleAuthResponse(data);
    } catch (error) {
      const code = error.data?.code;
      handleAlertMsg(
        "error",
        code === "INVALID_CREDENTIALS"
          ? "Incorrect login details."
          : error.data?.error || "Unable to login. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sendLoginOtp = async (e) => {
    e.preventDefault();
    if (!isMobileNumber(loginForm.identifier)) {
      handleAlertMsg("error", "Enter a valid 10-digit mobile number for OTP login.");
      return;
    }
    if (submitting || secondsLeft(resendAt) > 0) return;
    setSubmitting(true);
    setAccounts([]);
    setVerificationToken("");
    resetOtpFields();
    try {
      const data = await authPost("login/send-otp", {
        phone: loginForm.identifier.trim(),
      });
      const phone = data.phone || loginForm.identifier.trim();
      setOtpPhone(phone);
      setOtpStarted(true);
      setExpiresAt(Date.now() + Number(data.expires_in_seconds || 300) * 1000);
      setResendAt(Date.now() + Number(data.resend_after_seconds || 60) * 1000);
      handleAlertMsg("success", `We've sent a code to your WhatsApp on ${phone}.`);
    } catch (error) {
      handleAlertMsg("error", error.data?.error || "Unable to send OTP.");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyLoginOtp = async (otp) => {
    setSubmitting(true);
    try {
      const data = await authPost("login/verify-otp", { phone: otpPhone, otp });
      if (data.status === "select_account") {
        setVerificationToken(data.verification_token || "");
      }
      handleAuthResponse(data);
    } catch (error) {
      setInvalidOTP(true);
      setResetKey((prevKey) => prevKey + 1);
      const attempts = error.data?.attempts_remaining;
      handleAlertMsg(
        "error",
        attempts <= 2 && attempts >= 0
          ? `${error.data?.error || "Invalid OTP"} ${attempts} attempts left.`
          : error.data?.error || "Invalid OTP. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const selectOtpAccount = async (userId) => {
    if (!verificationToken) return;
    setSubmitting(true);
    try {
      const data = await authPost("login/select-account", {
        phone: otpPhone,
        verification_token: verificationToken,
        user_id: userId,
      });
      finishSession(data);
    } catch (error) {
      handleAlertMsg("error", error.data?.error || "Unable to select account.");
    } finally {
      setSubmitting(false);
    }
  };

  const navigatetoweb = () => navigate("/");
  const handleforgetpassword = () => {
    localStorage.setItem("forget-password", "patient");
    navigate("/forget-password");
  };

  return (
    <div
      className="login-page"
      style={{
        backgroundColor: configData.THEME_COLORS.BACKGROUND,
        width: "100%",
        minHeight: "100vh",
        paddingBottom: "50px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="text-center login-branding">
        <img
          onClick={navigatetoweb}
          src={"/logo-removebg.png"}
          className="login-logo"
          style={{ height: "160px", width: "160px", marginLeft: "20px", cursor: "pointer" }}
          width={200}
          alt="Myprega"
        />
      </div>
      <div className="text-center my-auto login-shell" style={{ position: "relative" }}>
        <div className="container login-container">
          <div className="row">
            <div className="col-md-6 offset-md-3 login-column">
              <form className="p-3 bg-white mx-auto login-form-card" onSubmit={handleLogin}>
                <div className="login-heart-badge" aria-hidden="true">❤</div>
                <h4 className="fw-bolder py-4 login-title">Welcome Back</h4>
                <p className="mx-auto login-subtitle" style={{ color: "#bac1cb" }}>
                  Welcome to Myprega app. Please put your login credentials below to start using App.
                </p>
                <label className="login-label">Username, phone number or email</label>
                <input
                  name="username"
                  autoComplete="username"
                  type="text"
                  className="my-4 login-input"
                  placeholder="Enter username, phone or email"
                  style={{ width: "50%", minWidth: "300px", height: "45px", borderRadius: "12px", border: "1px solid black", padding: 10, display: "block", margin: "auto" }}
                  value={loginForm.identifier}
                  onChange={(e) => {
                    setLoginForm({ ...loginForm, identifier: e.target.value });
                    setOtpStarted(false);
                    setAccounts([]);
                    setVerificationToken("");
                    resetOtpFields();
                  }}
                />
                <label className="login-label">Password</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  className="mb-2 login-input"
                  placeholder="Enter your password"
                  style={{ width: "50%", minWidth: "300px", height: "45px", borderRadius: "12px", border: "1px solid black", padding: 10, display: "block", margin: "0 auto" }}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                />
                <div className="mx-auto login-meta-row">
                  <div className="login-remember-col">
                    <input className="login-checkbox" type="checkbox" />
                    <span className="ps-2 login-remember-text">Remember me</span>
                  </div>
                  <div className="text-end login-forgot-row" style={{ color: "#ee5e8f", display: "flex", alignItems: "center", justifyContent: "flex-end", width: "auto" }}>
                    <div className="login-forgot-link" style={{ cursor: "pointer" }} onClick={handleforgetpassword}>
                      Forgot password?
                    </div>
                  </div>
                </div>
                <div className="text-center mt-3 login-otp-panel" style={{ width: "50%" }}>
                  <button type="button" className="login-otp-action" disabled={submitting || secondsLeft(resendAt) > 0} onClick={sendLoginOtp} style={{ fontSize: "13px", cursor: submitting ? "not-allowed" : "pointer", border: 0, background: "transparent" }}>
                    {otpStarted ? "Resend OTP" : "Log in with OTP"}
                  </button>
                  {secondsLeft(resendAt) > 0 && (
                    <p className="mt-3 login-otp-timer" style={{ fontSize: "13px" }}>
                      Request a new code in {secondsLeft(resendAt)} seconds
                    </p>
                  )}
                  {otpStarted && secondsLeft(expiresAt) > 0 && (
                    <p className="mt-3 login-otp-timer" style={{ fontSize: "13px" }}>
                      OTP expires in {secondsLeft(expiresAt)} seconds
                    </p>
                  )}
                  <div className="login-otp-input-wrap" style={{ marginTop: "20px", marginBottom: "10px" }}>
                    {otpStarted && secondsLeft(expiresAt) > 0 && accounts.length === 0 && (
                      <OTPInput length={6} onComplete={verifyLoginOtp} reset={invalidOTP} resetKey={resetKey} />
                    )}
                  </div>
                </div>
                {accounts.length > 0 && (
                  <div className="mx-auto my-3" style={{ width: "60%" }}>
                    {accounts.map((account) => (
                      <button
                        key={account.user_id}
                        type="button"
                        className="my-2 login-submit-btn"
                        style={{ width: "100%", minHeight: "40px" }}
                        disabled={submitting}
                        onClick={(e) =>
                          verificationToken
                            ? selectOtpAccount(account.user_id)
                            : handleLogin(e, account.user_id)
                        }
                      >
                        {account.fname || "Account"} - {account.role_name || account.role_id}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mx-auto login-actions-row">
                  <div className="login-button-col">
                    <button className="my-4 login login-submit-btn" disabled={submitting || !loginForm.identifier.trim() || !loginForm.password} style={{ width: "120px", height: "40px", borderRadius: "12px", cursor: submitting ? "not-allowed" : "pointer", backgroundColor: "#007bff", color: "white" }}>
                      Login
                    </button>
                  </div>
                </div>
                <div className="py-4 login-footer" style={{ color: "#bac1cb" }}>
                  Don&apos;t have an account?
                  <span className="ps-2 login-signup-link" style={{ color: "#ee5e8f", cursor: "pointer" }} onClick={() => navigate("/patient/Signup")}>
                    Sign up
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <AlertShow alertShowHide={showAlert} alertClose={() => setShowAlert(false)} setalertMsg={alertMsg} severity={severity} />
    </div>
  );
}

export default Loginform;
